import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Camera, Upload, ArrowLeft, CheckCircle2, Loader2 } from "lucide-react";
import { api, apiErr, uploadFile, imgSrc } from "@/lib/apiClient";
import { Loading } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const FIELD_LABELS = {
  company_name: "Business name",
  phone: "Phone",
  email: "Email",
  address: "Address",
  category: "Category",
  subcategory: "Subcategory",
  keywords: "Keywords",
};
const TEXT_FIELDS = ["company_name", "phone", "email", "address"];

// Visual confidence indicator. The real gate is server-side (admin threshold);
// this is informational so the submitter knows which fields to double-check.
const ConfidenceBadge = ({ value, threshold }) => {
  const v = Number(value ?? 0);
  const tone = v >= threshold ? "bg-brand-green/15 text-brand-green"
    : v >= 60 ? "bg-brand-gold/20 text-brand-gold-dark"
    : "bg-destructive/15 text-destructive";
  return (
    <span className={`ml-2 rounded-full px-2 py-0.5 text-[10px] font-bold ${tone}`} title="Extraction confidence">
      {v}%
    </span>
  );
};

export default function SnapBusiness() {
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [photoUrl, setPhotoUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [fields, setFields] = useState(null);
  const [confidences, setConfidences] = useState({});
  const [extracted, setExtracted] = useState(null);
  const [threshold, setThreshold] = useState(80);
  const [cats, setCats] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => { api.get("/categories").then(({ data }) => setCats(data)).catch(() => {}); }, []);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFile(file);
      setPhotoUrl(url);
      await runExtract(url);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const runExtract = async (url) => {
    setExtracting(true);
    try {
      const { data } = await api.post("/submissions/extract", { photo_url: url });
      setExtracted(data);
      setFields({ ...data.fields, keywords: (data.fields.keywords || []).join(", ") });
      setConfidences(data.confidences || {});
      setThreshold(data.confidence_threshold ?? 80);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setExtracting(false);
    }
  };

  const setField = (k, v) => setFields((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!fields?.company_name?.trim()) { toast.error("Business name is required"); return; }
    setSubmitting(true);
    try {
      const corrected = {
        ...fields,
        keywords: (fields.keywords || "").split(",").map((k) => k.trim()).filter(Boolean),
      };
      const { data } = await api.post("/submissions", {
        photo_url: photoUrl,
        extracted: {
          fields: extracted.fields,
          confidences: extracted.confidences,
          ad_layout_hints: extracted.ad_layout_hints,
        },
        corrected,
      });
      setDone(data);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setSubmitting(false);
    }
  };

  const subOptions = (() => {
    const cat = cats.find((c) => c.name === fields?.category);
    return cat?.subcategories || [];
  })();

  if (done) {
    return (
      <div className="mx-auto max-w-lg px-4 py-10 text-center">
        <CheckCircle2 className="mx-auto h-14 w-14 text-brand-green" />
        <h1 className="mt-4 text-2xl font-black">Submitted for review</h1>
        <p className="mt-2 text-muted-foreground">
          Thanks! <span className="font-semibold">{done.fields?.company_name}</span> was sent to our team.
          {done.needs_review
            ? " A couple of details were low-confidence, so an admin will double-check them before it goes live."
            : " Once approved, we'll invite the business to claim their profile."}
        </p>
        <div className="mt-6 flex justify-center gap-2">
          <Button variant="outline" onClick={() => { setDone(null); setPhotoUrl(""); setFields(null); setExtracted(null); }} data-testid="snap-another">Add another</Button>
          <Button className="bg-brand-green hover:bg-brand-green-dark" onClick={() => nav("/app")} data-testid="snap-home">Done</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg px-4 py-6">
      <button className="mb-4 flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" onClick={() => nav("/app")} data-testid="snap-back">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <h1 className="text-2xl font-black tracking-tight">Add a business from a photo</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Snap a business ad, sign, flyer, or card. We'll read the details for you to review.
      </p>

      {photoUrl && (
        <div className="mt-5 overflow-hidden rounded-[14px] border border-border">
          <img src={imgSrc(photoUrl)} alt="Business" className="max-h-64 w-full object-cover" />
        </div>
      )}

      {!fields && (
        <div className="mt-6">
          <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFile} data-testid="snap-file-input" />
          <Button
            className="w-full bg-brand-green hover:bg-brand-green-dark"
            disabled={uploading || extracting}
            onClick={() => fileRef.current?.click()}
            data-testid="snap-capture-btn"
          >
            {uploading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Uploading…</>
              : extracting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Reading photo…</>
              : <><Camera className="mr-2 h-4 w-4" /> Take / choose photo</>}
          </Button>
        </div>
      )}

      {extracting && !fields && <div className="mt-6"><Loading /></div>}

      {fields && (
        <div className="mt-6 space-y-4">
          <p className="text-xs text-muted-foreground">
            Review and fix anything that's wrong — corrections make future scans smarter.
          </p>
          {TEXT_FIELDS.map((k) => (
            <div key={k}>
              <label className="flex items-center text-xs font-bold uppercase tracking-[0.15em] text-brand-green">
                {FIELD_LABELS[k]}
                <ConfidenceBadge value={confidences[k]} threshold={threshold} />
              </label>
              <Input className="mt-1" value={fields[k] || ""} onChange={(e) => setField(k, e.target.value)} data-testid={`snap-field-${k}`} />
            </div>
          ))}

          <div>
            <label className="flex items-center text-xs font-bold uppercase tracking-[0.15em] text-brand-green">
              {FIELD_LABELS.category}
              <ConfidenceBadge value={confidences.category} threshold={threshold} />
            </label>
            <select
              className="mt-1 h-10 w-full rounded-md border border-border bg-background px-2 text-sm"
              value={cats.some((c) => c.name === fields.category) ? fields.category : ""}
              onChange={(e) => setField("category", e.target.value)}
              data-testid="snap-field-category"
            >
              <option value="">{fields.category ? `From photo: ${fields.category}` : "Select a category…"}</option>
              {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </div>

          {subOptions.length > 0 && (
            <div>
              <label className="flex items-center text-xs font-bold uppercase tracking-[0.15em] text-brand-green">
                {FIELD_LABELS.subcategory}
                <ConfidenceBadge value={confidences.subcategory} threshold={threshold} />
              </label>
              <select
                className="mt-1 h-10 w-full rounded-md border border-border bg-background px-2 text-sm"
                value={subOptions.some((s) => s.name === fields.subcategory) ? fields.subcategory : ""}
                onChange={(e) => setField("subcategory", e.target.value)}
                data-testid="snap-field-subcategory"
              >
                <option value="">Select…</option>
                {subOptions.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
              </select>
            </div>
          )}

          <div>
            <label className="flex items-center text-xs font-bold uppercase tracking-[0.15em] text-brand-green">
              {FIELD_LABELS.keywords}
              <ConfidenceBadge value={confidences.keywords} threshold={threshold} />
            </label>
            <Input className="mt-1" placeholder="comma, separated, keywords" value={fields.keywords || ""} onChange={(e) => setField("keywords", e.target.value)} data-testid="snap-field-keywords" />
          </div>

          <Button className="w-full bg-brand-green hover:bg-brand-green-dark" disabled={submitting} onClick={submit} data-testid="snap-submit">
            {submitting ? "Submitting…" : "Submit for review"}
          </Button>
        </div>
      )}
    </div>
  );
}
